import { useEffect, useRef } from 'react';

/**
 * Attaches an IntersectionObserver to the returned ref.
 * When the element enters the viewport, the class `scroll-visible` is added.
 * Pair with CSS classes: scroll-fade-up, scroll-fade-scale, scroll-slide-left.
 *
 * @param threshold - Fraction of element visible before triggering (default 0.05)
 * @param rootMargin - Margin offset (default "0px 0px -20px 0px" — small negative to trigger slightly before fully in view)
 */
export function useScrollAnimation<T extends HTMLElement = HTMLElement>(
  threshold = 0.05,
  rootMargin = '0px 0px -20px 0px'
) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // If already visible (e.g. element is in viewport on initial mount),
    // add scroll-visible immediately without waiting for observer callback.
    const rect = el.getBoundingClientRect();
    const alreadyVisible =
      rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      el.classList.add('scroll-visible');
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('scroll-visible');
          // Once visible, no need to keep observing
          observer.unobserve(el);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return ref;
}

/**
 * Observes multiple children of a container and adds `scroll-visible`
 * to each child when the container enters the viewport, with staggered timing.
 *
 * @param childSelector - CSS selector for the child elements to stagger
 * @param threshold - Intersection threshold (default 0.05)
 */
export function useStaggeredScrollAnimation<T extends HTMLElement = HTMLElement>(
  childSelector = '[data-stagger]',
  threshold = 0.05
) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    const children = Array.from(container.querySelectorAll<HTMLElement>(childSelector));

    const makeVisible = () => {
      children.forEach((child, i) => {
        const delay = i * 80;
        setTimeout(() => child.classList.add('scroll-visible'), delay);
      });
    };

    // If container is already in the viewport, trigger immediately.
    const rect = container.getBoundingClientRect();
    const alreadyVisible =
      rect.top < window.innerHeight && rect.bottom > 0;
    if (alreadyVisible) {
      makeVisible();
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          makeVisible();
          observer.unobserve(container);
        }
      },
      { threshold, rootMargin: '0px 0px -20px 0px' }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [childSelector, threshold]);

  return ref;
}
